import React, { useState, useRef, useEffect } from 'react';
import { User, Plus, Ticket, Clock } from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmContext';

// last_visit은 "YYYY-MM-DD HH:MM" 형식(서버 로컬 타임)으로 온다.
// new Date()로 파싱하면 타임존 처리가 브라우저마다 달라질 수 있어
// 문자열을 직접 쪼갠다. 올해 방문이면 연도를 생략해 짧게, 작년 이전
// 방문이면 연도를 붙여 어느 해인지 헷갈리지 않게 한다.
const formatVisitDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const [datePart, timePart] = dateTimeStr.split(' ');
  const [year, month, day] = (datePart || '').split('-');
  if (!year || !month || !day) return dateTimeStr;
  const isThisYear = parseInt(year, 10) === new Date().getFullYear();
  const datePrefix = isThisYear
    ? `${parseInt(month, 10)}/${parseInt(day, 10)}`
    : `${year}/${parseInt(month, 10)}/${parseInt(day, 10)}`;
  return timePart ? `${datePrefix} ${timePart}` : datePrefix;
};

const CustomerCard = ({
  customer,
  stampRules,
  isBusy,
  onStampAdd,
  onFetchCoupons,
  onCouponUse,
  compact = false,
  hideActions = false,
}) => {
  const confirm = useConfirm();
  const [couponOptions, setCouponOptions] = useState(null);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!couponOptions) return;
    const handleClickOutside = (e) => {
      // 쿠폰 사용 확인 모달(ConfirmContext)은 카드 DOM 밖에 별도로 렌더링돼
      // "바깥 클릭"으로 오인된다 - 취소를 눌러도 이 리스너가 먼저 반응해서
      // 펼쳐둔 쿠폰 목록이 같이 닫혀버렸다(취소했는데 목록까지 사라져
      // 헷갈린다는 피드백). 모달 안쪽 클릭은 무시한다.
      if (e.target.closest('[data-confirm-dialog]')) return;
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setCouponOptions(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [couponOptions]);

  // 서버(CardSize, services/customer.go)와 동일 - 실제 스탬프 카드
  // 이미지가 물리적으로 10칸 고정이라, 등록된 규칙이 몇 개든(예: 3/5/8개
  // 단계별) 진행률 바는 항상 이 값 하나로 고정한다. 예전엔 "다음 규칙
  // 단계"를 상한으로 써서 단계마다 바 눈금이 다시 스케일링됐는데(3/5,
  // 4/5, 6/8처럼), 카드 이미지 자체가 세분화돼 있지 않아 그렇게까지
  // 나눠 보여줄 필요가 없다는 피드백으로 단순화함.
  const CARD_SIZE = 10;
  const rules = stampRules || [];
  const hasRules = rules.length > 0;
  const nextTier = rules.find((r) => r.required_stamps > customer.stamps);
  const progressTarget = CARD_SIZE;

  const canUseCoupon = customer.available_coupons > 0;
  const busy = isBusy || loadingCoupons;

  const handleStampClick = async () => {
    if (busy) return;
    if (!(await confirm(`${customer.phone_short} 고객에게 스탬프를 적립하시겠습니까?`))) return;
    onStampAdd(customer.phone);
  };

  const handleUseClick = async () => {
    if (busy || !canUseCoupon) return;
    if (couponOptions) {
      setCouponOptions(null);
      return;
    }
    setLoadingCoupons(true);
    try {
      const coupons = await onFetchCoupons(customer.phone);
      if (coupons.length > 0) {
        setCouponOptions(coupons);
      }
    } finally {
      setLoadingCoupons(false);
    }
  };

  // 대시보드 "최근 방문"(hideActions) 전용 - 카드 전체를 눌러 보유 쿠폰
  // 목록만 펼쳐본다. 배지 하나만 누르게 하면 타겟이 작아 누르기 불편하다는
  // 이유로 카드 전체를 클릭 영역으로 넓혔다. handleUseClick과 fetch/toggle
  // 로직은 같지만, 여기서 펼쳐지는 목록은 조회 전용이다(아래 렌더링에서
  // hideActions일 때는 클릭해서 바로 사용 처리하는 버튼 대신 평범한
  // 목록으로 보여줌) - 이 위젯은 훑어보는 용도라 되돌릴 수 없는 사용
  // 처리는 고객관리 탭에서만 하게 하려는 의도다. canUseCoupon이 false면
  // handleUseClick이 스스로 아무것도 안 하므로 여기서 따로 막지 않는다.
  const handleCardClick = hideActions ? handleUseClick : undefined;

  const handlePick = async (number) => {
    if (busy) return;
    const picked = (couponOptions || []).find((c) => c.number === number);
    const couponLabel = picked ? `"${picked.coupon_type || '쿠폰'}"` : '이 쿠폰';
    const expiryLabel = picked ? `\n유효기간: ${picked.expiry_date || '무제한'}` : '';
    if (!(await confirm(`${couponLabel} 쿠폰을 사용 처리하시겠습니까?${expiryLabel}\n되돌릴 수 없습니다.`))) return;
    setCouponOptions(null);
    await onCouponUse(customer.phone, number);
  };

  const buttonPad = compact ? 'px-2.5 py-1.5' : 'px-3 py-2';

  return (
    <div
      ref={cardRef}
      onClick={handleCardClick}
      className={`rounded-xl border bg-white shadow-sm hover:shadow-md transition-all ${
        compact ? 'p-3' : 'p-4'
      } ${
        // 쓸 쿠폰이 있는 손님을 목록에서 한눈에 골라낼 수 있게 테두리
        // 색만 바꾼다 - 텍스트 배지만으로는 스캔하며 눈에 안 들어온다는
        // 피드백이 있었다.
        canUseCoupon
          ? 'border-rose-300 hover:border-rose-400'
          : 'border-gray-200 hover:border-gray-300'
      } ${handleCardClick && canUseCoupon ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className={`flex-shrink-0 rounded-full bg-blue-50 text-blue-500 flex items-center justify-center ${
              compact ? 'w-9 h-9' : 'w-11 h-11'
            }`}
          >
            <User className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-gray-900">{customer.phone_short}</span>
              {/* 쿠폰 유무와 무관하게 항상 자리를 차지해서, 목록을 훑을 때
                  이 배지가 있고 없고에 따라 줄마다 높이/구성이 들쭉날쭉해
                  보이던 문제를 없앤다. 없으면 배지 자체를 흐리게. 클릭
                  가능 영역은 배지가 아니라 카드 전체(handleCardClick)다. */}
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-full px-2 py-0.5 ${
                  canUseCoupon ? 'bg-rose-50 text-rose-600' : 'bg-gray-50 text-gray-300'
                }`}
              >
                <Ticket className="w-3 h-3" />
                쿠폰 {customer.available_coupons || 0}장
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
              <Clock className="w-3 h-3" />
              <span className="tabular-nums">
                {formatVisitDateTime(customer.last_visit)} 방문
                {' · '}총 {customer.visit_count}회
                {!compact && customer.avg_visit_interval_days != null &&
                  ` · 평균 ${Math.round(customer.avg_visit_interval_days)}일마다`}
              </span>
            </div>
          </div>
        </div>
        {!hideActions && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleStampClick}
              className={`flex items-center gap-1 ${buttonPad} bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors ${busy ? 'opacity-50 pointer-events-none' : ''}`}
            >
              <Plus className="w-4 h-4" />
              적립
            </button>
            {/* 쿠폰이 생겼을 때만 버튼이 갑자기 나타나던 것을 없앴다 - 항상
                그려두고, 쓸 쿠폰이 없을 때만 눌러도 반응 없는 회색으로 표시. */}
            <button
              onClick={handleUseClick}
              disabled={!canUseCoupon || busy}
              className={`flex items-center gap-1 ${buttonPad} text-sm font-medium rounded-lg transition-colors ${
                canUseCoupon
                  ? `bg-red-500 text-white hover:bg-red-600 ${busy ? 'opacity-50 pointer-events-none' : ''}`
                  : 'bg-gray-100 text-gray-300 cursor-not-allowed'
              }`}
            >
              <Ticket className="w-4 h-4" />
              쿠폰 사용
            </button>
          </div>
        )}
      </div>

      {/* 사용할 쿠폰 선택 - 눌러서 바로 사용 처리까지 한다(고객관리 탭 전용) */}
      {!hideActions && couponOptions && (
        <div className="mt-3 rounded-lg p-3 bg-rose-50 border border-rose-100">
          <div className="text-sm font-medium mb-2 text-rose-700">사용할 쿠폰을 선택하세요</div>
          <div className="space-y-1.5">
            {couponOptions.map((coupon) => (
              <button
                key={coupon.number}
                onClick={() => handlePick(coupon.number)}
                className="w-full text-left px-3 py-2 bg-white border border-rose-100 rounded-lg hover:bg-rose-100 hover:border-rose-200 text-sm flex justify-between items-center transition-colors"
              >
                <span className="font-medium text-gray-800">{coupon.coupon_type || '쿠폰'}</span>
                <span className="text-gray-400 text-xs">~{coupon.expiry_date}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 보유 쿠폰 조회 전용 - 대시보드 "최근 방문"에서 카드를 눌렀을 때.
          목록만 보여주고 클릭해도 사용 처리되지 않는다 - 되돌릴 수 없는
          동작은 고객관리 탭에서만 하도록 의도적으로 분리했다. 카드 전체가
          클릭 토글 영역이라, 내용을 읽다가 안쪽을 눌러도 바로 닫히지
          않도록 전파를 막는다. */}
      {hideActions && couponOptions && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="mt-3 rounded-lg p-3 bg-rose-50 border border-rose-100"
        >
          <div className="text-sm font-medium mb-2 text-rose-700">보유 쿠폰</div>
          <div className="space-y-1.5">
            {couponOptions.map((coupon) => (
              <div
                key={coupon.number}
                className="px-3 py-2 bg-white border border-rose-100 rounded-lg text-sm flex justify-between items-center"
              >
                <span className="font-medium text-gray-800">{coupon.coupon_type || '쿠폰'}</span>
                <span className="text-gray-400 text-xs">~{coupon.expiry_date}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 스탬프 진행률 (컴팩트 모드에서는 생략) */}
      {!compact && (
        <div className="mt-4">
          {!hasRules ? (
            <div className="text-xs text-gray-400 bg-gray-50 rounded-lg p-2.5 text-center">
              등록된 쿠폰이 없습니다. 설정 화면에서 스탬프 규칙을 등록해주세요.
            </div>
          ) : (
            <>
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span className="font-medium text-gray-600">스탬프 진행률</span>
                <span className="tabular-nums">{customer.stamps}/{progressTarget}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-600 h-2.5 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((customer.stamps / progressTarget) * 100, 100)}%` }}
                ></div>
              </div>
              <div className="text-right mt-1">
                <span className="text-xs text-gray-400">
                  {nextTier
                    ? `${nextTier.required_stamps - customer.stamps}개 더 모으면 ${nextTier.coupon_type}!`
                    : customer.stamps >= CARD_SIZE
                      ? '쿠폰 발급 가능!'
                      : `${CARD_SIZE - customer.stamps}개 더 모으면 카드가 초기화돼요`}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerCard;
